import { NetworkError, TimeoutError } from '../error';
import { watchClientCause } from './clientCause';

/** Request timeout, in ms. Bounds a single attempt, not a whole 401→refresh→retry sequence. */
export const DEFAULT_TIMEOUT_MS = 10_000;

// `AbortSignal.any` (Chrome <116) and `AbortSignal.timeout` (Chrome <103) are both missing on
// browsers we see in prod (HUB-55/HUB-59, WWP-655), and not only on the legacy bundle: Chrome
// 105-115 clears plugin-legacy's modern gate, which is where most of those events come from.
const anySignal = (signals: AbortSignal[]): AbortSignal => {
  if (typeof AbortSignal.any === 'function') {
    return AbortSignal.any(signals);
  }

  const controller = new AbortController();
  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort(signal.reason);
      break;
    }
    // The `signal` option (Chrome 90, so older than anything missing `AbortSignal.any`) drops the
    // listener once any signal wins, keeping a long-lived caller signal from collecting one per request.
    signal.addEventListener('abort', () => controller.abort(signal.reason), { once: true, signal: controller.signal });
  }
  return controller.signal;
};

/** Aborts with the same `TimeoutError` DOMException the native signal uses. */
const timeoutSignal = (ms: number): AbortSignal => {
  if (typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(ms);
  }

  const controller = new AbortController();
  // Uncleared on purpose, like the native signal (rationale at the call site).
  setTimeout(() => controller.abort(new DOMException('signal timed out', 'TimeoutError')), ms);
  return controller.signal;
};

/**
 * Lives apart from `transport.ts` on purpose: `runtime.ts` needs it, and `runtime.ts` is imported by
 * the generated `client.gen.ts` — so anything it pulls in must not import `client.gen` back.
 *
 * Bounds every attempt, retries included (they route through this same wrapper). Typed as
 * `typeof fetch` because that's the client's `fetch` option; the client only ever passes a `Request`.
 */
export const timeoutFetch: typeof fetch = async (input, init) => {
  const request = input instanceof Request ? input : new Request(input, init);
  const callerSignal = request.signal;
  // Deliberately a timeout signal, not a clearable setTimeout: the signal stays attached to the
  // response body after fetch() resolves, so it also bounds a stalled body read (a clear-on-settle timer
  // would leave that read to hang). The tradeoff — an uncancellable ~10s timer per request — is cheap.
  const timeout = timeoutSignal(DEFAULT_TIMEOUT_MS);
  const signal = callerSignal ? anySignal([callerSignal, timeout]) : timeout;
  const clientCause = watchClientCause();
  try {
    return await globalThis.fetch(request, { signal });
  } catch (error) {
    // A timeout that fires while the caller hasn't aborted is a TimeoutError, not a plain abort.
    if (timeout.aborted && !callerSignal?.aborted) {
      throw new TimeoutError(DEFAULT_TIMEOUT_MS, request.url, request.method, clientCause());
    }
    // `fetch` rejects with a bare TypeError when the request never reached the server
    // (offline, DNS, connection reset, CORS). Classify it so no caller has to sniff TypeError.
    if (error instanceof TypeError) {
      throw new NetworkError(error, request.url, request.method);
    }
    throw error;
  }
};
